// Libs
import classNames from 'classnames/bind';
import { Button, Flex, Heading, IconRefreshLine, Modal, Spinner, Text } from '@instructure/ui';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import 'react-quill/dist/quill.bubble.css';
import ReactQuill from 'react-quill';
// Components, Layouts, Pages
// Others
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { useCanvasSessionExpired } from '~/utils/hooks/canvasSession';
import { getListNotifications } from '~/thunks/notification/notificationThunk';
import { DEFAULT_NUMBER_ZERO, EMPTY_STRING } from '~/utils/constants/common';
import { INotifications } from '~/utils/interface/notifications';
// Styles, images, icons
import styles from './Announcement.module.scss';
import './Announcement.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const Announcement = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/home', 'common/base']);
  const dispatch = useAppDispatch();
  const isCanvasSessionExpired = useCanvasSessionExpired();
  //#endregion Declare Hook

  //#region Selector
  const notifications = useAppSelector((state) => state.notificationState.notifications);
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedNotification, setSelectedNotification] = useState<INotifications | null>(null);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListNotifications();
  }, []);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListNotifications = async () => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      await dispatch(getListNotifications()).unwrap();
    } catch (e) {
      setErrorMessage((e as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (notification: INotifications) => {
    setSelectedNotification(notification);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={cx('centerContainer')}>
          <Spinner size='small' />
        </div>
      );
    }

    if (errorMessage) {
      return (
        <div className={cx('centerContainer')}>
          <Flex justifyItems='center' margin='medium 0'>
            {isCanvasSessionExpired ? (
              <Text variant='contentSmall'>{t('canvas_session_expired_card_message', { ns: 'common/base' })}</Text>
            ) : (
              <Button
                onClick={handleGetListNotifications}
                size='medium'
                color='primary'
                margin='x-large 0 0 0'
                renderIcon={<IconRefreshLine />}
              >
                {t('retry_button_label', { ns: 'common/base' })}
              </Button>
            )}
          </Flex>
        </div>
      );
    }

    if (notifications.length === DEFAULT_NUMBER_ZERO) {
      return (
        <Flex display='flex' justifyItems='center' height='100%' alignItems='center'>
          <Text variant='contentSmall'>{t('no_announcements_available')}</Text>
        </Flex>
      );
    }

    return (
      <>
        <Flex display='flex' direction='row' alignItems='center' justifyItems='center' width={'100%'}>
          <Heading level='h3'>{t('announcement_board_titles')}</Heading>
        </Flex>

        <div className={cx('announcementContainer')}>
          {notifications.map((notification) => (
            <div key={notification.id} className={cx('announcementItem')}>
              <div className={cx('itemRow')} onClick={() => handleOpenModal(notification)}>
                <img
                  src={icons.commonIconAnnouncement}
                  alt={t('common_alt_img', { ns: 'common/base' })}
                  className={cx('iconAnnouncement')}
                />
                <div className={cx('itemCol')}>
                  <Text variant='contentImportant'>{notification.title}</Text>
                </div>
              </div>
              <hr className={cx('line')} />
            </div>
          ))}
        </div>
      </>
    );
  };
  //#endregion Handle Function

  return (
    <div className={cx('container')}>
      {renderContent()}

      <Modal
        open={isModalOpen}
        onDismiss={handleCloseModal}
        size='medium'
        shouldCloseOnDocumentClick={false}
        label={selectedNotification?.title || EMPTY_STRING}
      >
        <Modal.Header>
          <Flex alignItems='end' justifyItems='end'>
            <div className={cx('closeIcon')} onClick={handleCloseModal}>
              <img
                src={icons.commonIconClose}
                alt={t('common_alt_img', { ns: 'common/base' })}
                className={cx('closeIcon')}
              />
            </div>
          </Flex>
        </Modal.Header>
        <Modal.Body>
          <Heading level='h3' margin='0 0 small 0'>
            {selectedNotification?.title}
          </Heading>
          <ReactQuill value={selectedNotification?.message || EMPTY_STRING} theme='bubble' readOnly={true} />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Announcement;
